import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ACTIVE_STATUSES, type IssueScreenDto, type IssueStatus } from "@klantpunt/shared";
import { can, useMe } from "../../lib/auth";
import { api, ApiError } from "../../lib/api";
import { useToast } from "../../lib/toast";

/**
 * Bevinding op het lobbyscherm van het gebouw (docs/SCHERMEN.md): alleen met een eigen, publieke tekst. Een
 * opgeloste bevinding verdwijnt vanzelf van het scherm. Alleen voor wie het gebouw beheert.
 */
export function ScreenSection({ issueId, title, status }: { issueId: string; title: string; status: IssueStatus }) {
  const { data: me } = useMe();
  const manage = can(me, "building.manage");
  const q = useQuery({
    queryKey: ["issue-screen", issueId],
    queryFn: () => api<IssueScreenDto>(`/issues/${issueId}/screen`),
    enabled: manage,
  });
  if (!manage || !q.data || (!ACTIVE_STATUSES.has(status) && !q.data.text)) return null;
  return <ScreenForm key={q.data.text ?? ""} issueId={issueId} title={title} text={q.data.text} />;
}

function ScreenForm({ issueId, title, text }: { issueId: string; title: string; text: string | null }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [on, setOn] = useState(text !== null);
  const [value, setValue] = useState(text ?? title);
  const save = useMutation({
    mutationFn: (t: string | null) =>
      api<IssueScreenDto>(`/issues/${issueId}/screen`, { method: "PUT", body: { text: t } }),
    onSuccess: (r) => {
      qc.setQueryData(["issue-screen", issueId], r);
      toast(r.text ? "Staat op het scherm in de lobby" : "Niet meer op het scherm");
    },
    onError: (e) => toast(e instanceof ApiError ? e.message : "Opslaan mislukt"),
  });
  return (
    <section className="sec" aria-label="Op het scherm in de lobby">
      <label className="checkrow">
        <input
          type="checkbox"
          checked={on}
          disabled={save.isPending}
          onChange={(e) => {
            setOn(e.target.checked);
            if (!e.target.checked && text !== null) save.mutate(null);
          }}
        />
        <span>
          Op het scherm in de lobby
          <span className="muted small block">
            Bezoekers en huurders zien deze tekst tot de bevinding is opgelost. Schrijf hem zo dat iedereen hem mag
            lezen.
          </span>
        </span>
      </label>
      {on && (
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            if (value.trim()) save.mutate(value.trim());
          }}
        >
          <label className="field grow">
            <span className="label">Tekst op het scherm</span>
            <input value={value} maxLength={120} onChange={(e) => setValue(e.target.value)} />
          </label>
          <button
            type="submit"
            className="btn small"
            disabled={save.isPending || !value.trim() || value.trim() === text}
          >
            {save.isPending ? "Bezig met opslaan…" : "Opslaan"}
          </button>
        </form>
      )}
    </section>
  );
}
